import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { JobFormValues } from '../EditEnrichedVacancy.types';

import { useUnsavedChanges } from 'stores/unsaved-changes/hooks/useUnsavedChanges';
import { UnsavedChangesArea } from 'stores/unsaved-changes/unsaved-changes.types';
import { useContextSelector } from 'use-context-selector';
import { DEFAULT_JOB_FORM_VALUES } from '../../../utils/EnrichVacancyModal.constants';
import { EnrichVacancyModalContext } from '../../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EditEnrichedVacancyContext } from '../EditEnrichedVacancyContext/EditEnrichedVacancyContext';
import { useStyles } from './EnrichedVersion.styles';
import { EnrichedVersionRef } from './EnrichedVersion.types';
import { CompensationAndBenefits } from './FormSections/CompensationAndBenefits';
import { EmploymentRequirements } from './FormSections/EmploymentRequirements';
import { JobDetails } from './FormSections/JobDetails';
import { PositionOverview } from './FormSections/PositionOverview';
import { Requirements } from './FormSections/Requirements';
import { Responsibilities } from './FormSections/Responsibilities';

export const EnrichedVersion = ({ focusFormTab }: { focusFormTab: () => void }) => {
  const classes = useStyles();
  const { registerChangeDetector } = useUnsavedChanges([UnsavedChangesArea.EnrichVacancyModal_EnrichedVersion]);
  const enrichedVersionRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.enrichedVersion.ref);
  const initialData = useContextSelector(EditEnrichedVacancyContext, (s) => s.enrichedVersion.initialData);
  const setEnrichedVersionHasErrors = useContextSelector(
    EditEnrichedVacancyContext,
    (s) => s.setEnrichedVersionHasErrors,
  );
  const vacancy = useContextSelector(EnrichVacancyModalContext, (s) => s.vacancy);
  const initialDataRef = useRef<JobFormValues | null>(null);

  const isEnhancingVacancy = !vacancy?.is_improved;

  const initialFormData = useMemo(() => {
    if (!initialData) return DEFAULT_JOB_FORM_VALUES;

    const job_details = {
      ...initialData.job_details,
      job_title: initialData.job_details.job_title,
    };

    return { ...initialData, job_details };
  }, [initialData]);

  useEffect(() => {
    initialDataRef.current = cloneDeep(initialFormData);
  }, [initialFormData]);

  const enrichedVersionForm = useForm<JobFormValues>({ defaultValues: initialFormData });
  const hasErrors = Object.keys(enrichedVersionForm.errors).length > 0;

  useImperativeHandle(
    enrichedVersionRef,
    () =>
      ({
        getIsDirty: () => {
          return !isEqual(initialDataRef.current, enrichedVersionForm.getValues());
        },
        getValues: enrichedVersionForm.getValues,
        trigger: enrichedVersionForm.trigger,
        reset: (data) => {
          enrichedVersionForm.reset(data);

          setTimeout(() => {
            initialDataRef.current = enrichedVersionForm.getValues();
          }, 100);
        },
        focusFormTab,
      }) satisfies EnrichedVersionRef,
    [],
  );

  useEffect(() => {
    setEnrichedVersionHasErrors(hasErrors);
  }, [hasErrors]);

  useEffect(() => {
    return registerChangeDetector(() => {
      if (isEnhancingVacancy) return true;

      return enrichedVersionRef.current?.getIsDirty() ?? false;
    });
  }, []);

  return (
    <FormProvider {...enrichedVersionForm}>
      <form css={classes.previewContainer}>
        <JobDetails />
        <EmploymentRequirements />
        <PositionOverview />
        <Requirements />
        <Responsibilities />
        <CompensationAndBenefits />
      </form>
    </FormProvider>
  );
};
